import { type ReactElement } from 'react';
import {
  AppsRegular,
  CheckmarkCircleRegular,
  DeleteRegular,
  DocumentRegular,
  HistoryRegular,
  HomeRegular,
  KeyRegular,
  LaptopRegular,
  LockClosedRegular,
  OrganizationRegular,
  PeopleAddRegular,
  PeopleRegular,
  PeopleTeamRegular,
  PersonAddRegular,
  PersonDeleteRegular,
  PersonEditRegular,
  PersonProhibitedRegular,
  PersonSearchRegular,
  PersonSettingsRegular,
  ShieldCheckmarkRegular,
  ShieldRegular,
  ShieldKeyholeRegular,
  MapRegular,
  PulseRegular,
  CodeRegular,
  RocketRegular,
  CloudRegular,
} from '@fluentui/react-icons';
import { DashboardPage } from '../pages/DashboardPage';
import { UserInsightsPage } from '../pages/UserInsightsPage';
import { UserGraphPage } from '../pages/UserGraphPage';
import { IamPage } from '../pages/IamPage';
import { AssessmentPage } from '../pages/AssessmentPage';
import { PimPage } from '../pages/PimPage';
import { CreateUserPage } from '../pages/CreateUserPage';
import { BlockUserPage } from '../pages/BlockUserPage';
import { DeleteUserPage } from '../pages/DeleteUserPage';
import { CredentialsPage } from '../pages/CredentialsPage';
import { UserAttributesPage } from '../pages/UserAttributesPage';
import { GuestsCreatePage } from '../pages/GuestsCreatePage';
import { GuestsOverviewPage } from '../pages/GuestsOverviewPage';
import { GuestsEditPage } from '../pages/GuestsEditPage';
import { GroupsCreatePage } from '../pages/GroupsCreatePage';
import { GroupMembersPage } from '../pages/GroupMembersPage';
import { GroupsDeletePage } from '../pages/GroupsDeletePage';
import { TeamsCreatePage } from '../pages/TeamsCreatePage';
import { TeamsViewPage } from '../pages/TeamsViewPage';
import { TeamsDeletePage } from '../pages/TeamsDeletePage';
import { DevicesInventoryPage } from '../pages/DevicesInventoryPage';
import { LapsPage } from '../pages/LapsPage';
import { SoftwarePage } from '../pages/SoftwarePage';
import { AuditLogPage } from '../pages/AuditLogPage';
import { GovernancePage } from '../pages/GovernancePage';
import { SiteMapPage } from '../pages/SiteMapPage';
import { ActiveUsersPage } from '../pages/ActiveUsersPage';
import { DevIssuesPage } from '../pages/DevIssuesPage';
import { ChangelogPage } from '../pages/ChangelogPage';
import { OneDrivePage } from '../pages/OneDrivePage';
import { OneDriveOverviewPage } from '../pages/OneDriveOverviewPage';
import { MyRequestsPage } from '../pages/MyRequestsPage';
import { ApprovalsPage } from '../pages/ApprovalsPage';

export interface FeatureItem {
  key: string;
  text: string;
  icon: ReactElement;
  /** Stabiler Funktions-Schlüssel — gesperrt-/genehmigungsfähig (z. B. "users.delete"). */
  pageKey?: string;
  /** Noch nicht implementiert — zeigt „soon" und die ComingSoon-Seite. */
  soon?: boolean;
  /** Geplante Funktion (Anzeige auf der ComingSoon-Seite). */
  description?: string;
  /** Bereits gebaute Seite. */
  element?: ReactElement;
}

export interface FeatureSection {
  label: string;
  adminOnly?: boolean;
  items: FeatureItem[];
}

/**
 * Einzige Quelle der Navigationsstruktur. Seitennamen sind kurz gehalten — was
 * eine Seite tut, leitet sich aus der Kategorie ab (z. B. Benutzer → „Anlegen").
 */
export const featureNav: FeatureSection[] = [
  {
    label: 'Übersicht',
    items: [
      { key: '/', text: 'Dashboard', icon: <HomeRegular />, pageKey: 'overview.dashboard', element: <DashboardPage /> },
      { key: '/insights', text: 'User-Insights', icon: <PersonSearchRegular />, pageKey: 'overview.insights', element: <UserInsightsPage /> },
      { key: '/insights/graph', text: 'User-Graph', icon: <OrganizationRegular />, pageKey: 'overview.graph', element: <UserGraphPage /> },
      { key: '/assessment', text: 'M365-Assessment', icon: <ShieldCheckmarkRegular />, pageKey: 'assessment.view', element: <AssessmentPage /> },
      { key: '/onedrive', text: 'OneDrive-Übersicht', icon: <CloudRegular />, pageKey: 'onedrive.overview', element: <OneDriveOverviewPage /> },
    ],
  },
  {
    label: 'Benutzer',
    items: [
      { key: '/users/new', text: 'Anlegen', icon: <PersonAddRegular />, pageKey: 'users.create', element: <CreateUserPage /> },
      { key: '/users/edit', text: 'Anpassen', icon: <PersonSettingsRegular />, pageKey: 'users.update', element: <UserAttributesPage />, description: 'In Entra änderbare Verzeichnis-Attribute pflegen.' },
      { key: '/users/block', text: 'Sperren', icon: <PersonProhibitedRegular />, pageKey: 'users.block', element: <BlockUserPage /> },
      { key: '/users/delete', text: 'Löschen', icon: <PersonDeleteRegular />, pageKey: 'users.delete', element: <DeleteUserPage /> },
      { key: '/users/onedrive', text: 'OneDrive', icon: <CloudRegular />, pageKey: 'users.onedrive', element: <OneDrivePage /> },
    ],
  },
  {
    label: 'Gast',
    items: [
      { key: '/guests/new', text: 'Anlegen', icon: <PersonAddRegular />, pageKey: 'guests.create', element: <GuestsCreatePage />, description: 'B2B-Einladung mit zeitlich begrenzter Gültigkeit.' },
      { key: '/guests', text: 'Übersicht', icon: <PeopleRegular />, pageKey: 'guests.overview', element: <GuestsOverviewPage />, description: 'Alle Gast-Einladungen mit Firma, Mail und letzter Anmeldung.' },
      { key: '/guests/edit', text: 'Ändern', icon: <PersonEditRegular />, pageKey: 'guests.edit', element: <GuestsEditPage />, description: 'Gültigkeit anpassen oder Einladung widerrufen.' },
    ],
  },
  {
    label: 'Gruppen',
    items: [
      { key: '/groups/new', text: 'Erstellen', icon: <PeopleAddRegular />, pageKey: 'groups.create', element: <GroupsCreatePage /> },
      { key: '/groups/members', text: 'Mitglieder', icon: <PeopleRegular />, pageKey: 'groups.members', element: <GroupMembersPage /> },
      { key: '/groups/delete', text: 'Löschen', icon: <DeleteRegular />, pageKey: 'groups.delete', element: <GroupsDeletePage /> },
    ],
  },
  {
    label: 'Teams',
    items: [
      { key: '/teams/new', text: 'Erstellen', icon: <PeopleTeamRegular />, pageKey: 'teams.create', element: <TeamsCreatePage /> },
      { key: '/teams', text: 'Anpassen', icon: <PeopleTeamRegular />, pageKey: 'teams.view', element: <TeamsViewPage /> },
      { key: '/teams/delete', text: 'Löschen', icon: <DeleteRegular />, pageKey: 'teams.delete', element: <TeamsDeletePage /> },
    ],
  },
  {
    label: 'Geräte',
    items: [
      { key: '/devices/inventory', text: 'Inventar', icon: <LaptopRegular />, pageKey: 'devices.inventory', element: <DevicesInventoryPage />, description: 'Geräte eines Users inkl. Compliance, OS-Version und Patch-Stand.' },
      { key: '/devices/laps', text: 'LAPS', icon: <LockClosedRegular />, pageKey: 'devices.laps', element: <LapsPage />, description: 'Lokales Admin-Passwort der Geräte eines Users einsehen (mit Audit-Warnung).' },
      { key: '/devices/software', text: 'Software', icon: <AppsRegular />, pageKey: 'devices.software', element: <SoftwarePage />, description: 'Software über Gruppenmitgliedschaft zuweisen.' },
    ],
  },
  {
    label: 'Danger-Zone',
    items: [
      {
        key: '/users/credentials',
        text: 'Passwort & MFA',
        icon: <KeyRegular />,
        pageKey: 'users.credentials',
        element: <CredentialsPage />,
        description: 'Passwort- oder MFA-Reset und Temporary Access Pass ausstellen.',
      },
      { key: '/danger/pim', text: 'PIM', icon: <ShieldKeyholeRegular />, pageKey: 'pim.view', element: <PimPage /> },
    ],
  },
  {
    label: 'App-Zugriffe',
    items: [
      { key: '/access/iam', text: 'IAM', icon: <KeyRegular />, pageKey: 'access.iam', element: <IamPage />, description: 'Rollen anlegen, Personen/Gruppen zuweisen, App-Zugriffe, Intune-Apps & Enterprise-App-Rollen berechtigen und den Soll-Zustand nach Entra anwenden.' },
    ],
  },
  {
    label: 'Freigaben',
    items: [
      { key: '/requests', text: 'Meine Anfragen', icon: <DocumentRegular />, pageKey: 'requests.mine', element: <MyRequestsPage /> },
      { key: '/approvals', text: 'Zu genehmigen', icon: <CheckmarkCircleRegular />, pageKey: 'requests.approvals', element: <ApprovalsPage /> },
    ],
  },
  {
    label: 'Regelwerk',
    adminOnly: true,
    items: [
      { key: '/activity', text: 'Aktive Nutzer', icon: <PulseRegular />, pageKey: 'activity.view', element: <ActiveUsersPage /> },
      { key: '/audit', text: 'Audit-Log', icon: <HistoryRegular />, pageKey: 'audit.view', element: <AuditLogPage /> },
      { key: '/settings/governance', text: 'Governance', icon: <ShieldRegular />, pageKey: 'governance.admin', element: <GovernancePage /> },
      { key: '/sitemap', text: 'Site-Map', icon: <MapRegular />, pageKey: 'architecture.view', element: <SiteMapPage /> },
    ],
  },
  {
    label: 'Dev',
    adminOnly: true,
    items: [
      { key: '/dev/issues', text: 'Feature Request & Bugs', icon: <CodeRegular />, pageKey: 'dev.issues', element: <DevIssuesPage /> },
      { key: '/dev/changelog', text: 'Update-Verlauf', icon: <RocketRegular />, pageKey: 'dev.changelog', element: <ChangelogPage /> },
    ],
  },
];

export interface GateableFunction {
  pageKey: string;
  label: string;
}

/** Funktionen, die per In-App-Berechtigung gesperrt werden können (mit Kategorie-Kontext). */
export const gateableFunctions: GateableFunction[] = featureNav.flatMap((section) =>
  section.items
    .filter((i): i is FeatureItem & { pageKey: string } => Boolean(i.pageKey))
    .map((i) => ({ pageKey: i.pageKey, label: `${section.label} – ${i.text}` })),
);
